/**
 * Cattle Management System
 * Complete CRUD operations with RFID integration, search, filtering, and detailed cattle records
 * Supports offline mode with cached data fallback
 */
import { gauShalaApi } from '@/services/gaushala/api';
import { offlineCattleApi } from '@/lib/offline';
import { useOffline } from '@/contexts/OfflineContext';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Calendar,
  Users,
  Activity,
  Wifi,
  AlertTriangle,
  CheckCircle,
  Camera,
  X,
  Save,
  Scan,
  Circle,
  Scale,
  History,
  Clock,
  LogIn,
  LogOut,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  Columns,
  Eye as EyeIcon,
  EyeOff,
  MapPinned,
  Tag,
  Cpu,
  Palette
} from 'lucide-react';
import {
  cattleApi,
  masterDataApi,
  gaushalaAccessApi,
  healthRecordsApi,
  calculateAgeFromDob,
  type Cattle,
  type Breed,
  type Species,
  type Gender,
  type Color,
  type HealthRecord,
} from '../../services/gaushala/api';
import { getLoggedInUserGaushalaId, getCurrentUserContext } from '../../utils/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import CowDungTransaction from '../../components/gaushala/CowDungTransaction';
import TransactionHistory from '../../components/gaushala/TransactionHistory';

interface LanguageContextType {
  language: 'hi' | 'en';
  setLanguage: (lang: 'hi' | 'en') => void;
  t: (key: string) => string;
}

// Legacy interface for backwards compatibility with transaction components
interface CattleLegacy {
  id: string;
  rfidTag: string;
  name: string;
  breed: string;
  age: number;
  weight: number;
  health: 'healthy' | 'sick' | 'under_treatment';
  owner: string;
  ownerId: string;
  currentStatus: 'IN' | 'OUT' | 'UNKNOWN';
  location: {
    latitude: number;
    longitude: number;
    timestamp: number;
    address?: string;
  };
  totalDungCollected: number;
  lastDungCollection?: number;
  isActive: boolean;
  photoUrl?: string;
  medicalHistory: MedicalRecord[];
  createdAt: number;
  updatedAt: number;
}

interface MedicalRecord {
  id: string;
  date: number;
  type: 'checkup' | 'vaccination' | 'treatment' | 'surgery';
  description: string;
  veterinarian: string;
  medication?: string;
  nextCheckup?: number;
}

interface AttendanceLog {
  id: string;
  cattleId: string;
  timestamp: number;
  type: 'IN' | 'OUT';
  location?: string;
  rfidScanned?: boolean;
}

interface DailyAttendance {
  date: string;
  firstIn?: AttendanceLog;
  lastOut?: AttendanceLog;
  totalLogs: AttendanceLog[];
}

interface AttendanceHistory {
  cattleId: string;
  days: DailyAttendance[];
  selectedDate: string;
}

interface CattleFilter {
  search: string;
  health: string;
  breed: string;
  owner: string;
  status: string;
  isActive: boolean | null;
  gaushalaId?: string;
}

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

type SortableColumn =
  | 'name'
  | 'rfidTag'
  | 'breed'
  | 'species'
  | 'gender'
  | 'age'
  | 'weight'
  | 'health'
  | 'status'
  | 'color'
  | 'location'
  | 'earTag'
  | 'microchip'
  | 'createdDate';

interface ColumnConfig {
  key: SortableColumn;
  label: string;
  visible: boolean;
  order: number;
}

const translations = {
  en: {
    title: 'Cattle Management',
    subtitle: 'Manage cattle records with RFID tracking',
    addCattle: 'Add Cattle',
    searchPlaceholder: 'Search: Name, Gender, Breed, RFID, Animal ID, Weight, or Age (e.g., "jersey", "125 kg", "3 years")',
    filterBy: 'Filter',
    allHealth: 'All Health',
    healthy: 'Healthy',
    sick: 'Sick',
    under_treatment: 'Under Treatment',
    allBreeds: 'All Breeds',
    allOwners: 'All Owners',
    active: 'Active',
    inactive: 'Inactive',
    name: 'Name',
    rfidTag: 'RFID Tag',
    breed: 'Breed',
    age: 'Age',
    weight: 'Weight',
    health: 'Health',
    status: 'Status',
    statusIn: 'Inside',
    statusOut: 'Outside',
    statusUnknown: 'Unknown',
    allStatus: 'All Status',
    owner: 'Owner',
    lastCollection: 'Last Collection',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    recordTransaction: 'Record Dung Collection',
    viewHistory: 'View Transaction History',
    scanRFID: 'Scan RFID',
    addPhoto: 'Add Photo',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    cattleDetails: 'Cattle Details',
    basicInfo: 'Basic Information',
    locationInfo: 'Location Information',
    collectionStats: 'Collection Statistics',
    medicalHistory: 'Medical History',
    latitude: 'Latitude',
    longitude: 'Longitude',
    address: 'Address',
    totalCollected: 'Total Collected',
    avgQuality: 'Average Quality',
    lastCheckup: 'Last Checkup',
    nextCheckup: 'Next Checkup',
    kg: 'kg',
    years: 'years',
    never: 'Never',
    editCattle: 'Edit Cattle',
    deleteCattle: 'Delete Cattle',
    confirmDelete: 'Are you sure you want to delete this cattle record?',
    yes: 'Yes',
    no: 'No',
    attendance: 'Attendance',
    viewAttendance: 'View Attendance',
    attendanceLogs: 'Attendance Logs',
    dailyAttendance: 'Daily Attendance',
    firstIn: 'First In',
    lastOut: 'Last Out',
    totalEntries: 'Total Entries',
    viewLogs: 'View Logs',
    timeIn: 'Time In',
    timeOut: 'Time Out',
    noAttendanceData: 'No attendance data available',
    today: 'Today',
    close: 'Close',
    selectDate: 'Select Date',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    attendanceHistory: 'Attendance History',
    noDataForDate: 'No attendance data for this date',
    present: 'Present',
    absent: 'Absent',
    dayView: 'Day View'
  },
  hi: {
    title: 'पशु प्रबंधन',
    subtitle: 'RFID ट्रैकिंग के साथ पशु रिकॉर्ड प्रबंधन',
    addCattle: 'पशु जोड़ें',
    searchPlaceholder: 'खोजें: नाम, लिंग, नस्ल, RFID, पशु ID, वजन, या उम्र (उदा: "jersey", "125 kg", "3 years")',
    filterBy: 'फ़िल्टर',
    allHealth: 'सभी स्वास्थ्य',
    healthy: 'स्वस्थ',
    sick: 'बीमार',
    under_treatment: 'उपचाराधीन',
    allBreeds: 'सभी नस्लें',
    allOwners: 'सभी मालिक',
    active: 'सक्रिय',
    inactive: 'निष्क्रिय',
    name: 'नाम',
    rfidTag: 'RFID टैग',
    breed: 'नस्ल',
    age: 'आयु',
    weight: 'वज़न',
    health: 'स्वास्थ्य',
    owner: 'मालिक',
    lastCollection: 'अंतिम संग्रह',
    actions: 'कार्य',
    view: 'देखें',
    edit: 'संपादित करें',
    delete: 'हटाएं',
    recordTransaction: 'गोबर संग्रह रिकॉर्ड करें',
    viewHistory: 'लेनदेन इतिहास देखें',
    scanRFID: 'RFID स्कैन करें',
    addPhoto: 'फोटो जोड़ें',
    saveChanges: 'परिवर्तन सेव करें',
    cancel: 'रद्द करें',
    cattleDetails: 'पशु विवरण',
    basicInfo: 'मूलभूत जानकारी',
    locationInfo: 'स्थान की जानकारी',
    collectionStats: 'संग्रह आंकड़े',
    medicalHistory: 'चिकित्सा इतिहास',
    latitude: 'अक्षांश',
    longitude: 'देशांतर',
    address: 'पता',
    totalCollected: 'कुल संग्रहीत',
    avgQuality: 'औसत गुणवत्ता',
    lastCheckup: 'अंतिम जांच',
    nextCheckup: 'अगली जांच',
    kg: 'किलो',
    years: 'वर्ष',
    never: 'कभी नहीं',
    editCattle: 'पशु संपादित करें',
    deleteCattle: 'पशु हटाएं',
    confirmDelete: 'क्या आप वाकई इस पशु रिकॉर्ड को हटाना चाहते हैं?',
    yes: 'हाँ',
    no: 'नहीं',
    attendance: 'उपस्थिति',
    viewAttendance: 'उपस्थिति देखें',
    attendanceLogs: 'उपस्थिति लॉग',
    dailyAttendance: 'दैनिक उपस्थिति',
    firstIn: 'पहली प्रविष्टि',
    lastOut: 'अंतिम निकास',
    totalEntries: 'कुल प्रविष्टियां',
    viewLogs: 'लॉग देखें',
    timeIn: 'प्रवेश समय',
    timeOut: 'निकास समय',
    noAttendanceData: 'कोई उपस्थिति डेटा उपलब्ध नहीं',
    today: 'आज',
    close: 'बंद करें',
    selectDate: 'तारीख चुनें',
    last7Days: 'पिछले 7 दिन',
    last30Days: 'पिछले 30 दिन',
    attendanceHistory: 'उपस्थिति इतिहास',
    noDataForDate: 'इस तारीख के लिए कोई उपस्थिति डेटा नहीं',
    present: 'उपस्थित',
    absent: 'अनुपस्थित',
    dayView: 'दिन दृश्य'
  }
};

interface Props {
  languageContext: LanguageContextType;
}

export default function CattleManagement({ languageContext }: Props) {
  const { language } = languageContext;
  const navigate = useNavigate();

  // User context
  const userContext = getCurrentUserContext();
  const userGaushalaId = getLoggedInUserGaushalaId();

  // State management
  const [cattle, setCattle] = useState<Cattle[]>([]);
  const [filteredCattle, setFilteredCattle] = useState<Cattle[]>([]);
  const [loading, setLoading] = useState(true);
  const [originalCattle, setOriginalCattle] = useState<Cattle[]>([]); // Store full list for search restoration

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Master data state
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [gaushalas, setGaushalas] = useState<{ id: number; name: string; address: string }[]>([]);

  // Health records state for critical status detection
  const [healthRecordsByCattle, setHealthRecordsByCattle] = useState<Map<number, HealthRecord[]>>(new Map());

  // Modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showAttendanceLogsModal, setShowAttendanceLogsModal] = useState(false);
  const [selectedCattle, setSelectedCattle] = useState<Cattle | null>(null);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceHistory | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceLog[]>([]);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logsSearchTerm, setLogsSearchTerm] = useState('');
  const logsPerPage = 8;
  const [editFormData, setEditFormData] = useState<any>({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Filter state
  const [filter, setFilter] = useState<CattleFilter>({
    search: '',
    health: '',
    breed: '',
    owner: '',
    status: '',
    isActive: null,
    gaushalaId: undefined
  });

  // CRITICAL FIX: Separate input value state from filter state
  // This prevents input from losing focus when filter.search updates
  const [searchInputValue, setSearchInputValue] = useState('');

  // Ref for search input to maintain focus during search
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Track if we're currently in search mode (to prevent effects from interfering)
  const [isSearching, setIsSearching] = useState(false);

  // Sort state - supports multiple column sorting
  const [sortConfig, setSortConfig] = useState<SortConfig[]>([]);

  // Column visibility state
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<SortableColumn | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<SortableColumn | null>(null);
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Default columns configuration - 14 total columns
    const defaultColumns: ColumnConfig[] = [
      { key: 'name', label: 'Name', visible: true, order: 0 },
      { key: 'rfidTag', label: 'RFID Tag', visible: true, order: 1 },
      { key: 'breed', label: 'Breed', visible: true, order: 2 },
      { key: 'species', label: 'Species', visible: false, order: 3 },
      { key: 'gender', label: 'Gender', visible: true, order: 4 },
      { key: 'age', label: 'Age', visible: true, order: 5 },
      { key: 'weight', label: 'Weight', visible: true, order: 6 },
      { key: 'health', label: 'Health', visible: true, order: 7 },
      { key: 'status', label: 'Status', visible: false, order: 8 },
      { key: 'color', label: 'Color', visible: false, order: 9 },
      { key: 'location', label: 'Location', visible: false, order: 10 },
      { key: 'earTag', label: 'Ear Tag', visible: false, order: 11 },
      { key: 'microchip', label: 'Microchip', visible: false, order: 12 },
      { key: 'createdDate', label: 'Created Date', visible: false, order: 13 },
    ];

    // Try to load from localStorage first
    const saved = localStorage.getItem('cattleTableColumns');
    const savedVersion = localStorage.getItem('cattleTableColumnsVersion');
    const CURRENT_VERSION = '2.1'; // Increment when adding new columns

    if (saved && savedVersion === CURRENT_VERSION) {
      try {
        const parsed = JSON.parse(saved);
        // Verify all 14 columns exist
        if (parsed.length === 14) {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse saved columns:', e);
      }
    }

    // If no saved data or version mismatch, use defaults and save them
    localStorage.setItem('cattleTableColumns', JSON.stringify(defaultColumns));
    localStorage.setItem('cattleTableColumnsVersion', CURRENT_VERSION);
    return defaultColumns;
  });

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  /**
   * Load cattle from Gaushala Service with pagination
   * Automatically filters by user's gaushalaId if available
   * Supports offline mode with cached data fallback
   * MUST BE DEFINED BEFORE useEffect HOOKS
   */
  const loadCattleData = useCallback(async (page: number, size: number) => {
    try {
      setLoading(true);

      // Check if we're online
      const isOnline = navigator.onLine;

      if (isOnline) {
        // Online: Fetch cattle from Gaushala Service (port 8086) with pagination
        const response = await cattleApi.getAllCattle(page, size, 'createdAt');

        if (response.success && response.data) {
          const allCattle = response.data.content || [];

          // Note: Backend already filters by user's gaushalaId via JWT
          // So we don't need client-side filtering
          console.log(`[CattleManagement] Online - Loaded ${allCattle.length} cattle records (page ${page + 1} of ${response.data.totalPages})`);

          setCattle(allCattle);
          // Store original full cattle list for search clearing (avoid reload on search clear)
          if (page === 0) {
            setOriginalCattle(allCattle);
          }
          setTotalElements(response.data.totalElements || 0);
          setTotalPages(response.data.totalPages || 0);
          setCurrentPage(page);
        } else {
          console.error('Failed to fetch cattle data:', response.error);
          // Try offline fallback
          await loadCattleFromCache();
        }
      } else {
        // Offline: Load from cache
        await loadCattleFromCache();
      }
    } catch (error) {
      console.error('Error loading cattle data:', error);
      // Try offline fallback on any error
      await loadCattleFromCache();
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load cattle from offline cache
   * Used when offline or when online fetch fails
   */
  const loadCattleFromCache = useCallback(async () => {
    try {
      console.log('[CattleManagement] Loading cattle from offline cache...');
      const cachedCattle = await offlineCattleApi.getAll(0, 1000);

      if (cachedCattle && cachedCattle.length > 0) {
        console.log(`[CattleManagement] Offline - Loaded ${cachedCattle.length} cattle from cache`);
        setCattle(cachedCattle as Cattle[]);
        setOriginalCattle(cachedCattle as Cattle[]);
        setTotalElements(cachedCattle.length);
        setTotalPages(1); // All data in one page when offline
        setCurrentPage(0);
      } else {
        console.log('[CattleManagement] No cached cattle data available');
        setCattle([]);
        setTotalElements(0);
        setTotalPages(0);
      }
    } catch (cacheError) {
      console.error('[CattleManagement] Error loading from cache:', cacheError);
      setCattle([]);
      setTotalElements(0);
      setTotalPages(0);
    }
  }, []);

  /**
   * Load all health records and group by cattle ID
   * Used to detect critical health status (3+ sick records in 30 days)
   */
  const loadHealthRecords = useCallback(async () => {
    try {
      // Fetch all health records (up to 1000 for now)
      const response = await healthRecordsApi.getAllHealthRecords(0, 1000);

      if (response.success && response.data?.content) {
        // Group health records by cattle ID
        const recordsMap = new Map<number, HealthRecord[]>();

        response.data.content.forEach((record: HealthRecord) => {
          if (record.cattleId) {
            const existing = recordsMap.get(record.cattleId) || [];
            existing.push(record);
            recordsMap.set(record.cattleId, existing);
          }
        });

        setHealthRecordsByCattle(recordsMap);
        console.log(`Loaded health records for ${recordsMap.size} cattle`);
      }
    } catch (error) {
      console.error('Error loading health records:', error);
    }
  }, []);

  /**
   * Server-side search - fetches ALL matching cattle from backend
   * This searches across ALL pages, not just the current page
   * Results are sorted by latest first (createdAt DESC)
   * When offline, performs client-side search on cached data
   */
  const handleServerSearch = useCallback(async (searchTerm: string) => {
    try {
      // DO NOT set loading=true here - it causes the input to unmount/remount
      // Keep the search bar visible while searching for better UX
      setIsSearching(true); // Mark that we're in search mode
      console.log('🔍 [SEARCH] Starting search for:', searchTerm);
      console.log('🔍 [SEARCH] Search term length:', searchTerm.length);

      const isOnline = navigator.onLine;

      if (isOnline) {
        // Online: Call the search/all endpoint to get complete results from ALL pages
        const response = await cattleApi.searchAllCattle(searchTerm);

        console.log('📋 [SEARCH] Server response:', response);
        console.log('📋 [SEARCH] Response success:', response.success);
        console.log('📋 [SEARCH] Response data type:', typeof response.data);
        console.log('📋 [SEARCH] Response data is array:', Array.isArray(response.data));

        if (response.success && Array.isArray(response.data)) {
          const allResults = response.data;
          console.log(`✅ [SEARCH] Search found ${allResults.length} matching cattle records across ALL pages`);
          console.log(`✅ [SEARCH] Sample results:`, allResults.slice(0, 3));

          setCattle(allResults);
          setFilteredCattle(allResults); // Set filtered cattle to show all search results
          setTotalElements(allResults.length);
          setTotalPages(allResults.length > 0 ? 1 : 0); // All results on one page
          // NOTE: Do NOT set currentPage here - it would trigger pagination effect and reload original data!
          // Only update currentPage if it's not already 0
          setCurrentPage(prevPage => prevPage === 0 ? prevPage : 0);

          if (allResults.length === 0) {
            console.warn('⚠️ [SEARCH] No cattle found matching search term');
          }
        } else if (response.success && (!response.data || response.data.length === 0)) {
          console.warn('⚠️ [SEARCH] Search returned empty results');
          setCattle([]);
          setFilteredCattle([]);
          setTotalElements(0);
          setTotalPages(0);
        } else {
          console.error('❌ [SEARCH] Search failed:', response.error);
          console.error('❌ [SEARCH] Response:', response);
          // Fall back to offline search
          await performOfflineSearch(searchTerm);
        }
      } else {
        // Offline: Perform client-side search on cached data
        await performOfflineSearch(searchTerm);
      }
    } catch (error) {
      console.error('❌ [SEARCH] Error during server-side search:', error);
      // Try offline search as fallback
      await performOfflineSearch(searchTerm);
    } finally {
      // Don't call setLoading(false) - we never set it to true
      setIsSearching(false); // Exit search mode
    }
  }, []);

  /**
   * Perform offline search on cached cattle data
   */
  const performOfflineSearch = useCallback(async (searchTerm: string) => {
    try {
      console.log('🔍 [OFFLINE SEARCH] Searching cached data for:', searchTerm);
      const cachedCattle = await offlineCattleApi.getAll(0, 1000);

      if (cachedCattle && cachedCattle.length > 0) {
        const term = searchTerm.toLowerCase();
        const results = (cachedCattle as Cattle[]).filter(cattle =>
          cattle.name?.toLowerCase().includes(term) ||
          cattle.rfidTag?.toLowerCase().includes(term) ||
          cattle.tagNumber?.toLowerCase().includes(term) ||
          String(cattle.id).includes(term)
        );

        console.log(`✅ [OFFLINE SEARCH] Found ${results.length} matching cattle in cache`);
        setCattle(results);
        setFilteredCattle(results);
        setTotalElements(results.length);
        setTotalPages(results.length > 0 ? 1 : 0);
      } else {
        console.log('[OFFLINE SEARCH] No cached data to search');
        setCattle([]);
        setFilteredCattle([]);
        setTotalElements(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('[OFFLINE SEARCH] Error:', error);
      setCattle([]);
      setFilteredCattle([]);
      setTotalElements(0);
      setTotalPages(0);
    }
  }, []);

  /**
   * Apply client-side filters to cattle list
   * NOTE: Search is now handled server-side, so this only applies health/breed/status filters
   * MUST BE DEFINED BEFORE useEffect HOOKS
   */
  const applyFilters = useCallback(() => {
    let filtered = [...cattle];

    // Health status filter
    if (filter.health) {
      filtered = filtered.filter(c => getHealthStatus(c) === filter.health);
    }

    // Breed filter (by breed name)
    if (filter.breed) {
      filtered = filtered.filter(c => getBreedName(c.breedId) === filter.breed);
    }

    // Active status filter
    if (filter.isActive !== null) {
      filtered = filtered.filter(c => c.isActive === filter.isActive);
    }

    // Gaushala filter - already filtered at load time by userGaushalaId
    // Additional gaushalaId filter can be applied if needed
    if (filter.gaushalaId) {
      filtered = filtered.filter(c => c.gaushalaId === parseInt(filter.gaushalaId));
    }

    setFilteredCattle(filtered);
  }, [cattle, filter]);

  // ============================================================================
  // ALL FUNCTION DEFINITIONS COMPLETE - useEffect hooks follow
  // ============================================================================

  // Load cattle data, master data, and health records on component mount
  useEffect(() => {
    console.log('📋 [INIT] Component mounted, loading initial cattle data');
    loadCattleData(0, 20);
    loadMasterData();
    loadHealthRecords(); // Load health records for critical status detection
  }, []);

  // Listen for navigation from AddCattle - reload data when returning with shouldRefresh flag
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { shouldRefresh?: boolean; refreshSheds?: boolean };
    if (state?.shouldRefresh) {
      console.log('📋 [REFRESH] Reloading cattle data after save');
      // Reset to page 1 when returning from create
      setCurrentPage(0);
      loadCattleData(0, 20);

      // ✅ Invalidate shed data so ShedList refreshes next time it's viewed
      if (state.refreshSheds) {
        console.log('📋 [REFRESH] Setting shed data invalidation flag');
        localStorage.setItem('shedDataInvalidated', 'true');
      }
    }
  }, [location.state]);

  // Reload data when page or page size changes (but skip initial load and search mode)
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    // Only reload if:
    // 1. Component initialized (past first load)
    // 2. NOT in search mode (to prevent overwriting search results)
    if (hasInitialized && !isSearching) {
      console.log(`📄 [PAGINATION] Loading page ${currentPage} with size ${pageSize}`);
      loadCattleData(currentPage, pageSize);
    } else if (!hasInitialized) {
      setHasInitialized(true);
    }
    // NOTE: isSearching NOT in dependency array to prevent re-trigger when search mode ends
    // This is intentional - we only care about currentPage/pageSize changes for pagination
  }, [currentPage, pageSize]);

  // Apply ONLY non-search filters (health, breed, status)
  // IMPORTANT: Do NOT include filter.search in dependencies to prevent re-runs while typing
  // Search results are already filtered on server-side, no client-side filtering needed
  useEffect(() => {
    // Only apply filters if NOT actively searching
    // Once cattle is updated from search, it already contains only search results
    if (!filter.search || filter.search.trim().length === 0) {
      applyFilters();
    } else {
      // During search, cattle already contains filtered results from server
      // No need to apply additional client-side filters
      setFilteredCattle(cattle);
    }
    // ONLY watch non-search filters - this prevents re-runs when user types
  }, [cattle, filter.health, filter.breed, filter.isActive, filter.gaushalaId]);

  // Synchronize searchInputValue to filter.search with debounce
  // This keeps the input element stable while search updates happen asynchronously
  useEffect(() => {
    console.log('📝 [INPUT] searchInputValue changed:', searchInputValue);

    // Debounce the update to filter.search
    const timer = setTimeout(() => {
      console.log('🔄 [INPUT] Updating filter.search to:', searchInputValue);
      setFilter(prev => ({ ...prev, search: searchInputValue }));
    }, 500);

    return () => {
      console.log('⏹️ [INPUT] Canceling input sync timer');
      clearTimeout(timer);
    };
  }, [searchInputValue]);

  // Handle server-side search when search term changes (with debounce)
  useEffect(() => {
    console.log('🔔 [SEARCH] useEffect triggered, filter.search:', filter.search);

    // Debounce search - wait 500ms after user stops typing
    const searchTimer = setTimeout(() => {
      const searchTerm = (filter.search || '').trim();
      console.log('⏱️ [SEARCH] Debounce timer fired with term:', searchTerm);

      if (searchTerm.length > 0) {
        // Use server-side search to get ALL matching results
        console.log('🚀 [SEARCH] Executing search for:', searchTerm);
        handleServerSearch(searchTerm);
      } else {
        // If search is cleared, restore original data without reloading
        console.log('🔄 [SEARCH] Search cleared, restoring original list without server reload');
        setCurrentPage(0);
        setIsSearching(false); // Exit search mode
        // Restore from original data - no server call needed (instant, smooth restoration)
        if (originalCattle.length > 0) {
          setCattle(originalCattle);
          setFilteredCattle(originalCattle);
          setTotalElements(originalCattle.length);
          setTotalPages(Math.ceil(originalCattle.length / 20)); // Use default pageSize of 20
          setLoading(false); // Clear loading state immediately since we're restoring from cache
        }
      }
    }, 500); // Wait 500ms after user stops typing

    // Cleanup: cancel the timer if user types again
    return () => {
      console.log('⏰ [SEARCH] Canceling search timer');
      clearTimeout(searchTimer);
    };
  }, [filter.search]);

  // NOTE: Focus management effect removed
  // The input naturally retains focus during re-renders when there's minimal DOM churn
  // By fixing the filter effect dependencies (no longer watching filter.search),
  // we prevent excessive re-renders and the input naturally stays focused

  // Close column selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showColumnSelector && !target.closest('.column-selector-container')) {
        setShowColumnSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnSelector]);

  /**
   * Load master data for displaying breed/species/gender/color names
   */
  const loadMasterData = async () => {
    try {
      const [breedsRes, speciesRes, gendersRes, colorsRes, gaushalasRes] = await Promise.all([
        masterDataApi.getAllBreeds(),
        masterDataApi.getAllSpecies(),
        masterDataApi.getAllGenders(),
        masterDataApi.getAllColors(),
        gaushalaAccessApi.getAllGaushalas(),
      ]);

      if (breedsRes.success && breedsRes.data) setBreeds(breedsRes.data);
      if (speciesRes.success && speciesRes.data) setSpecies(speciesRes.data);
      if (gendersRes.success && gendersRes.data) setGenders(gendersRes.data);
      if (colorsRes.success && colorsRes.data) setColors(colorsRes.data);
      if (gaushalasRes.success && gaushalasRes.data) {
        // Map gaushala data to the format we need (id and name)
        const gaushalaList = gaushalasRes.data.map((g: any) => ({
          id: g.id,
          name: g.name || g.gaushalaName || `Gaushala #${g.id}`,
          address: g.registeredAddress || g.geocodedAddress || g.address || ''
        }));
        setGaushalas(gaushalaList);
      }
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  /**
   * Helper functions to get master data names by ID
   */
  const getBreedName = (breedId: number): string => {
    const breed = breeds.find(b => b.id === breedId);
    return breed?.name || `Breed #${breedId}`;
  };

  const getSpeciesName = (speciesId: number): string => {
    const sp = species.find(s => s.id === speciesId);
    return sp?.name || `Species #${speciesId}`;
  };

  const getGenderName = (genderId: number): string => {
    const gender = genders.find(g => g.id === genderId);
    return gender?.name || `Gender #${genderId}`;
  };

  const getColorName = (colorId: number): string => {
    const color = colors.find(c => c.id === colorId);
    return color?.name || `Color #${colorId}`;
  };

  const getGaushalaName = (gaushalaId: number): string => {
    const gaushala = gaushalas.find(g => g.id === gaushalaId);
    return gaushala?.name || `Gaushala #${gaushalaId}`;
  };

  const getGaushalaAddress = (gaushalaId: number): string => {
    const gaushala = gaushalas.find(g => g.id === gaushalaId);
    return gaushala?.address || 'N/A';
  };

  /**
   * Calculate search relevance score for ranking results
   */
  const calculateRelevanceScore = (cattle: Cattle, searchTerms: string[]): number => {
    let score = 0;
    const lowerName = cattle.name?.toLowerCase() || '';
    const lowerUniqueId = cattle.uniqueAnimalId?.toLowerCase() || '';
    const lowerRfid = cattle.rfidTagNo?.toLowerCase() || '';

    searchTerms.forEach(term => {
      // Exact matches get highest score
      if (lowerName === term) score += 100;
      if (lowerUniqueId === term) score += 100;
      if (lowerRfid === term) score += 100;

      // Starts with match gets high score
      if (lowerName.startsWith(term)) score += 50;
      if (lowerUniqueId.startsWith(term)) score += 50;
      if (lowerRfid.startsWith(term)) score += 50;

      // Contains match gets medium score
      if (lowerName.includes(term)) score += 25;
      if (lowerUniqueId.includes(term)) score += 25;
      if (lowerRfid.includes(term)) score += 25;

      // Master data matches get lower score
      if (getBreedName(cattle.breedId).toLowerCase().includes(term)) score += 20;
      if (getSpeciesName(cattle.speciesId).toLowerCase().includes(term)) score += 20;
      if (getGenderName(cattle.genderId).toLowerCase().includes(term)) score += 15;
      if (getColorName(cattle.colorId).toLowerCase().includes(term)) score += 15;

      // Tag matches
      if (cattle.earTagNo?.toLowerCase().includes(term)) score += 30;
      if (cattle.microchipNo?.toLowerCase().includes(term)) score += 30;
      if (cattle.shedNumber?.toLowerCase().includes(term)) score += 10;

      // Numeric field matches
      if (cattle.weight?.toString().includes(term)) score += 10;
      if (calculateAgeFromDob(cattle.dob).toString().includes(term)) score += 10;

      // Health status match
      if (getHealthStatus(cattle).toLowerCase().includes(term)) score += 15;
    });

    return score;
  };

  /**
   * Handle column sorting with multi-column support
   * Click: Set single column sort (toggle asc/desc)
   * Shift+Click: Add column to multi-column sort
   */
  const handleSort = (column: SortableColumn, shiftKey: boolean = false) => {
    setSortConfig(prevConfig => {
      if (shiftKey) {
        // Multi-column sorting (Shift+Click)
        const existingIndex = prevConfig.findIndex(s => s.column === column);

        if (existingIndex >= 0) {
          // Column already in sort - toggle direction or remove
          const existing = prevConfig[existingIndex];
          if (existing.direction === 'asc') {
            // Change to desc
            const newConfig = [...prevConfig];
            newConfig[existingIndex] = { column, direction: 'desc' };
            return newConfig;
          } else {
            // Remove this sort column
            return prevConfig.filter((_, i) => i !== existingIndex);
          }
        } else {
          // Add new column to sort (asc by default)
          return [...prevConfig, { column, direction: 'asc' }];
        }
      } else {
        // Single column sorting (regular click)
        const existing = prevConfig.find(s => s.column === column);

        if (existing) {
          // Toggle direction or clear if already desc
          if (existing.direction === 'asc') {
            return [{ column, direction: 'desc' }];
          } else {
            return []; // Clear sort
          }
        } else {
          // New sort column
          return [{ column, direction: 'asc' }];
        }
      }
    });
  };

  /**
   * Get sort indicator for a column
   */
  const getSortIndicator = (column: SortableColumn) => {
    const sortIndex = sortConfig.findIndex(s => s.column === column);

    if (sortIndex === -1) {
      return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    }

    const sort = sortConfig[sortIndex];
    const isMulti = sortConfig.length > 1;

    return (
      <div className="flex items-center gap-1">
        {sort.direction === 'asc' ? (
          <ArrowUp className="h-3 w-3 text-blue-600" />
        ) : (
          <ArrowDown className="h-3 w-3 text-blue-600" />
        )}
        {isMulti && (
          <span className="text-xs font-bold text-blue-600 bg-blue-100 rounded-full w-4 h-4 flex items-center justify-center">
            {sortIndex + 1}
          </span>
        )}
      </div>
    );
  };

  /**
   * Get visible columns sorted by order
   */
  const getVisibleColumns = (): ColumnConfig[] => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  };

  /**
   * Toggle column visibility
   */
  const toggleColumnVisibility = (key: SortableColumn) => {
    const newColumns = columns.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
    localStorage.setItem('cattleTableColumns', JSON.stringify(newColumns));
    localStorage.setItem('cattleTableColumnsVersion', '2.0');
  };

  /**
   * Reset columns to default
   */
  const resetColumns = () => {
    const defaultColumns: ColumnConfig[] = [
      { key: 'name', label: 'Name', visible: true, order: 0 },
      { key: 'rfidTag', label: 'RFID Tag', visible: true, order: 1 },
      { key: 'breed', label: 'Breed', visible: true, order: 2 },
      { key: 'species', label: 'Species', visible: false, order: 3 },
      { key: 'gender', label: 'Gender', visible: true, order: 4 },
      { key: 'age', label: 'Age', visible: true, order: 5 },
      { key: 'weight', label: 'Weight', visible: true, order: 6 },
      { key: 'health', label: 'Health', visible: true, order: 7 },
      { key: 'status', label: 'Status', visible: false, order: 8 },
      { key: 'color', label: 'Color', visible: false, order: 9 },
      { key: 'location', label: 'Location', visible: false, order: 10 },
      { key: 'earTag', label: 'Ear Tag', visible: false, order: 11 },
      { key: 'microchip', label: 'Microchip', visible: false, order: 12 },
      { key: 'createdDate', label: 'Created Date', visible: false, order: 13 },
    ];
    setColumns(defaultColumns);
    localStorage.setItem('cattleTableColumns', JSON.stringify(defaultColumns));
    localStorage.setItem('cattleTableColumnsVersion', '2.0');
  };

  /**
   * Handle drag start for column reordering
   */
  const handleDragStart = (key: SortableColumn) => {
    setDraggedColumn(key);
  };

  /**
   * Handle drag over for column reordering
   */
  const handleDragOver = (e: React.DragEvent, key: SortableColumn) => {
    e.preventDefault();
    setDragOverColumn(key);
  };

  /**
   * Handle drop for column reordering
   */
  const handleDrop = (targetKey: SortableColumn) => {
    if (!draggedColumn || draggedColumn === targetKey) {
      setDraggedColumn(null);
      setDragOverColumn(null);
      return;
    }

    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex(col => col.key === draggedColumn);
    const targetIndex = newColumns.findIndex(col => col.key === targetKey);

    // Swap orders
    const draggedCol = newColumns[draggedIndex];
    const targetCol = newColumns[targetIndex];

    const tempOrder = draggedCol.order;
    draggedCol.order = targetCol.order;
    targetCol.order = tempOrder;

    setColumns(newColumns);
    localStorage.setItem('cattleTableColumns', JSON.stringify(newColumns));
    localStorage.setItem('cattleTableColumnsVersion', '2.0');
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  /**
   * Handle drag end
   */
  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  /**
   * Apply sorting to filtered cattle
   */
  const applySorting = (cattleList: Cattle[]): Cattle[] => {
    if (sortConfig.length === 0) return cattleList;

    return [...cattleList].sort((a, b) => {
      for (const { column, direction } of sortConfig) {
        let compareResult = 0;

        switch (column) {
          case 'name':
            compareResult = a.name.localeCompare(b.name);
            break;
          case 'rfidTag':
            compareResult = (a.rfidTagNo || '').localeCompare(b.rfidTagNo || '');
            break;
          case 'breed':
            compareResult = getBreedName(a.breedId).localeCompare(getBreedName(b.breedId));
            break;
          case 'gender':
            compareResult = getGenderName(a.genderId).localeCompare(getGenderName(b.genderId));
            break;
          case 'age':
            compareResult = calculateAgeFromDob(a.dob) - calculateAgeFromDob(b.dob);
            break;
          case 'weight':
            compareResult = (a.weight || 0) - (b.weight || 0);
            break;
          case 'health':
            compareResult = getHealthStatus(a).localeCompare(getHealthStatus(b));
            break;
          case 'status':
            compareResult = (getIsActive(a) ? 1 : 0) - (getIsActive(b) ? 1 : 0);
            break;
          case 'species':
            compareResult = getSpeciesName(a.speciesId).localeCompare(getSpeciesName(b.speciesId));
            break;
          case 'color':
            compareResult = getColorName(a.colorId).localeCompare(getColorName(b.colorId));
            break;
          case 'location':
            compareResult = getGaushalaAddress(a.gaushalaId).localeCompare(getGaushalaAddress(b.gaushalaId));
            break;
          case 'earTag':
            compareResult = (a.earTagNo || '').localeCompare(b.earTagNo || '');
            break;
          case 'microchip':
            compareResult = (a.microchipNo || '').localeCompare(b.microchipNo || '');
            break;
          case 'createdDate':
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            compareResult = dateA - dateB;
            break;
        }

        if (compareResult !== 0) {
          return direction === 'asc' ? compareResult : -compareResult;
        }
      }

      return 0;
    });
  };

  // Helper function to derive active/inactive status from cattle data
  const getIsActive = (cattle: Cattle): boolean => {
    // If vaccination status is completed/current, consider as active
    if (cattle.vaccinationStatus) {
      const status = cattle.vaccinationStatus.toLowerCase();
      if (status.includes('completed') || status.includes('current')) {
        return true;
      }
      if (status.includes('pending') || status.includes('overdue')) {
        return false;
      }
    }

    // If no vaccination status, default to active unless explicitly disabled
    return true;
  };

  // Helper function to derive health status from cattle data
  const getHealthStatus = (cattle: Cattle): string => {
    // Use disabilityStatus field directly (set by user during cattle registration/edit)
    if (cattle.disabilityStatus) {
      const status = cattle.disabilityStatus.toUpperCase();
      if (status === 'SICK') return 'sick';
      if (status === 'UNDER_TREATMENT') return 'under_treatment';
      if (status === 'HEALTHY') return 'healthy';
    }

    // Fallback: default to healthy if no status set
    return 'healthy';
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'bg-green-100 text-green-800';
      case 'sick':
        return 'bg-red-100 text-red-800';
      case 'under_treatment':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IN':
        return 'bg-green-100 text-green-800';
      case 'OUT':
        return 'bg-orange-100 text-orange-800';
      case 'UNKNOWN':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN');
  };

  const handleView = (cattle: Cattle) => {
    navigate(`/gaushala/cattle/view/${cattle.id}`);
  };

  const handleEdit = (cattle: Cattle) => {
    navigate(`/gaushala/cattle/edit/${cattle.id}`);
  };

  const handleEditInputChange = (field: string, value: string) => {
    if (field.startsWith('location.')) {
      const locationField = field.split('.')[1];
      setEditFormData((prev: any) => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value
        }
      }));
    } else {
      setEditFormData((prev: any) => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedCattle) return;

    setIsUpdating(true);
    try {
      // Prepare update data in the format expected by the API
      const updateData = {
        ...editFormData,
        age: parseInt(editFormData.age),
        weight: parseFloat(editFormData.weight),
        location: {
          latitude: parseFloat(editFormData.location.latitude),
          longitude: parseFloat(editFormData.location.longitude),
          address: editFormData.location.address,
          timestamp: Date.now()
        }
      };

      const response = await gauShalaApi.cattle.updateCattle(selectedCattle.id, updateData);

      if (response.success) {
        // Update the cattle in the local state
        setCattle(prevCattle =>
          prevCattle.map(c =>
            c.id === selectedCattle.id
              ? { ...c, ...updateData, updatedAt: Date.now() }
              : c
          )
        );
        setShowEditModal(false);
        setSelectedCattle(null);
        setEditFormData({});
        alert('Cattle updated successfully!');
      } else {
        alert('Failed to update cattle: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update cattle:', error);
      alert('Failed to update cattle. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (cattle: Cattle) => {
    if (confirm(t('confirmDelete'))) {
      try {
        const response = await gauShalaApi.cattle.deleteCattle(cattle.id);

        if (response.success) {
          setCattle(prev => prev.filter(c => c.id !== cattle.id));
          console.log('✅ [DELETE] Cattle deleted successfully - invalidating shed data');
          // Invalidate shed data since occupancy has changed
          localStorage.setItem('shedDataInvalidated', 'true');
        } else {
          console.error('Failed to delete cattle:', response.error);
          alert('Failed to delete cattle. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting cattle:', error);
        alert('Error deleting cattle. Please try again.');
      }
    }
  };

  const handleRecordTransaction = (cattle: Cattle) => {
    setSelectedCattle(cattle);
    setShowTransactionModal(true);
  };

  const handleViewHistory = (cattle: Cattle) => {
    setSelectedCattle(cattle);
    setShowHistoryModal(true);
  };

  const handleTransactionSuccess = (transaction: any) => {
    // Update cattle data with latest collection info
    setCattle(prevCattle =>
      prevCattle.map(c =>
        c.id === selectedCattle?.id
          ? {
              ...c,
              totalDungCollected: c.totalDungCollected + parseFloat(transaction.weightKg || '0'),
              lastDungCollection: Date.now()
            }
          : c
      )
    );

    console.log('Transaction recorded successfully:', transaction);
  };

  // Generate mock attendance data for multiple days
  const generateMockAttendanceHistory = (cattleId: string): AttendanceHistory => {
    const days: DailyAttendance[] = [];
    const today = new Date();

    // Generate data for last 30 days
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toISOString().split('T')[0];

      // Random chance of attendance (85% chance of being present)
      const isPresent = Math.random() > 0.15;

      if (isPresent) {
        const logs: AttendanceLog[] = [];

        // Generate first in time (6-8 AM)
        const firstInTime = new Date(date);
        firstInTime.setHours(6 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);

        // Generate last out time (17-19 PM)
        const lastOutTime = new Date(date);
        lastOutTime.setHours(17 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60), 0, 0);

        const firstInLog: AttendanceLog = {
          id: `${cattleId}-in-${date.getTime()}-${i}`,
          cattleId,
          timestamp: firstInTime.getTime(),
          type: 'IN',
          location: 'Main Gate',
          rfidScanned: true
        };

        const lastOutLog: AttendanceLog = {
          id: `${cattleId}-out-${date.getTime()}-${i}`,
          cattleId,
          timestamp: lastOutTime.getTime(),
          type: 'OUT',
          location: 'Main Gate',
          rfidScanned: true
        };

        // Generate intermediate logs (2-5 entries)
        const numIntermediateLogs = 2 + Math.floor(Math.random() * 4);
        for (let j = 0; j < numIntermediateLogs; j++) {
          const randomTime = new Date(date);
          randomTime.setHours(9 + j * 2, Math.floor(Math.random() * 60), 0, 0);

          const locations = ['Feeding Area', 'Water Point', 'Milking Shed', 'Rest Area', 'Medical Check'];

          logs.push({
            id: `${cattleId}-${j}-${date.getTime()}-${i}`,
            cattleId,
            timestamp: randomTime.getTime(),
            type: j % 2 === 0 ? 'OUT' : 'IN',
            location: locations[j % locations.length],
            rfidScanned: Math.random() > 0.1 // 90% RFID scan success
          });
        }

        logs.push(firstInLog, lastOutLog);
        logs.sort((a, b) => a.timestamp - b.timestamp);

        days.push({
          date: dateString,
          firstIn: firstInLog,
          lastOut: lastOutLog,
          totalLogs: logs
        });
      } else {
        // Absent day
        days.push({
          date: dateString,
          firstIn: undefined,
          lastOut: undefined,
          totalLogs: []
        });
      }
    }

    return {
      cattleId,
      days: days.reverse(), // Most recent first
      selectedDate: today.toISOString().split('T')[0]
    };
  };

  const handleViewAttendance = async (cattle: Cattle) => {
    setSelectedCattle(cattle);

    // Generate mock data for multiple days - replace with actual API call
    const historyData = generateMockAttendanceHistory(cattle.id);
    setAttendanceHistory(historyData);
    setSelectedDate(historyData.selectedDate);
    setShowAttendanceModal(true);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleViewAttendanceLogs = () => {
    if (attendanceHistory && selectedDate) {
      const dayData = attendanceHistory.days.find(day => day.date === selectedDate);
      if (dayData) {
        setAttendanceLogs(dayData.totalLogs);
        setLogsCurrentPage(1);
        setLogsSearchTerm('');
        setShowAttendanceLogsModal(true);
      }
    }
  };

  const getCurrentDayAttendance = (): DailyAttendance | null => {
    if (attendanceHistory && selectedDate) {
      return attendanceHistory.days.find(day => day.date === selectedDate) || null;
    }
    return null;
  };

  const getFilteredLogs = () => {
    if (!logsSearchTerm.trim()) return attendanceLogs;

    return attendanceLogs.filter(log =>
      log.type.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
      log.location?.toLowerCase().includes(logsSearchTerm.toLowerCase()) ||
      new Date(log.timestamp).toLocaleTimeString().includes(logsSearchTerm)
    );
  };

  const getPaginatedLogs = () => {
    const filtered = getFilteredLogs();
    const startIndex = (logsCurrentPage - 1) * logsPerPage;
    const endIndex = startIndex + logsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredLogs().length / logsPerPage);
  };

  /**
   * Render cell content based on column key
   */
  const renderCellContent = (column: SortableColumn, cattle: Cattle) => {
    switch (column) {
      case 'name':
        return (
          <div className="flex items-center">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
              {cattle.photoUrl ? (
                <img
                  src={cattle.photoUrl}
                  alt={cattle.name}
                  className="h-10 w-10 rounded-full object-cover"
                  onError={(e) => {
                    // Fallback to cow emoji if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = '<span class="text-2xl">🐄</span>';
                  }}
                />
              ) : (
                <span className="text-2xl">🐄</span>
              )}
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">{cattle.name}</div>
              <div className="text-sm text-gray-500">ID: {cattle.uniqueAnimalId}</div>
            </div>
          </div>
        );

      case 'rfidTag':
        return (
          <div className="flex items-center">
            <Wifi className="h-4 w-4 text-blue-500 mr-2" />
            <span className="text-sm font-mono text-gray-900">{cattle.rfidTagNo || 'No RFID'}</span>
          </div>
        );

      case 'breed':
        return <span className="text-sm text-gray-900">{getBreedName(cattle.breedId)}</span>;

      case 'gender':
        const genderName = getGenderName(cattle.genderId).toLowerCase();
        if (genderName.includes('female') || genderName.includes('cow')) {
          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-pink-100">
                <span className="text-sm">♀</span>
              </div>
              <span className="text-sm text-gray-900">{getGenderName(cattle.genderId)}</span>
            </div>
          );
        } else if (genderName.includes('male') || genderName.includes('bull')) {
          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100">
                <span className="text-sm">♂</span>
              </div>
              <span className="text-sm text-gray-900">{getGenderName(cattle.genderId)}</span>
            </div>
          );
        } else {
          return <span className="text-sm text-gray-900">{getGenderName(cattle.genderId)}</span>;
        }

      case 'age':
        return <span className="text-sm text-gray-900">{calculateAgeFromDob(cattle.dob)} {t('years')}</span>;

      case 'weight':
        return <span className="text-sm text-gray-900">{cattle.weight || 'N/A'} {cattle.weight ? t('kg') : ''}</span>;

      case 'health':
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getHealthColor(getHealthStatus(cattle))}`}>
            {t(getHealthStatus(cattle))}
          </span>
        );

      case 'status':
        return (
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${getIsActive(cattle) ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getIsActive(cattle) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {getIsActive(cattle) ? t('active') : t('inactive')}
            </span>
          </div>
        );

      case 'species':
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-900">{getSpeciesName(cattle.speciesId)}</span>
          </div>
        );

      case 'color':
        return (
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-purple-500" />
            <span className="text-sm text-gray-900">{getColorName(cattle.colorId)}</span>
          </div>
        );

      case 'location':
        return (
          <div className="flex items-start gap-2">
            <MapPinned className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <span
              className="text-sm text-gray-900 break-words"
              title={getGaushalaAddress(cattle.gaushalaId)}
            >
              {getGaushalaAddress(cattle.gaushalaId)}
            </span>
          </div>
        );

      case 'earTag':
        return (
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-orange-500" />
            <span className="text-sm font-mono text-gray-900">{cattle.earTagNo || 'N/A'}</span>
          </div>
        );

      case 'microchip':
        return (
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-teal-500" />
            <span className="text-sm font-mono text-gray-900">{cattle.microchipNo || 'N/A'}</span>
          </div>
        );

      case 'createdDate':
        return (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-indigo-500" />
            <span className="text-sm text-gray-900">
              {cattle.createdAt ? new Date(cattle.createdAt).toLocaleDateString() : 'N/A'}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const handleScanRFID = async () => {
    try {
      const response = await gauShalaApi.cattle.scanRfid();

      if (response.success && response.data) {
        const { rfidTag, cattleInfo } = response.data;
        if (cattleInfo) {
          alert(`RFID Scanned: ${rfidTag}\nCattle Found: ${cattleInfo.name}`);
        } else {
          alert(`RFID Scanned: ${rfidTag}\nNo cattle found with this tag.`);
        }
      } else {
        console.error('Failed to scan RFID:', response.error);
        alert('Failed to scan RFID. Please try again.');
      }
    } catch (error) {
      console.error('Error scanning RFID:', error);
      alert('Error scanning RFID. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('title')}</h2>
          <p className="text-gray-600">{t('subtitle')}</p>
        </div>
        <button
          onClick={() => navigate('/gaushala/cattle/add')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('addCattle')}
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchInputValue && (
            <button
              onClick={() => {
                setSearchInputValue('');
                setFilter(prev => ({ ...prev, search: '' }));
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Health Filter */}
          <select
            value={filter.health}
            onChange={(e) => setFilter(prev => ({ ...prev, health: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('allHealth')}</option>
            <option value="healthy">{t('healthy')}</option>
            <option value="sick">{t('sick')}</option>
            <option value="under_treatment">Under Treatment</option>
          </select>

          {/* Breed Filter */}
          <select
            value={filter.breed}
            onChange={(e) => setFilter(prev => ({ ...prev, breed: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('allBreeds')}</option>
            {breeds.map(breed => (
              <option key={breed.id} value={breed.name}>{breed.name}</option>
            ))}
          </select>

          {/* RFID Scan Button */}
          <button
            onClick={handleScanRFID}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Scan className="h-4 w-4" />
            {t('scanRFID')}
          </button>
        </div>

        {/* Column Selector Button */}
        <div className="mt-4 flex justify-end">
          <div className="relative column-selector-container">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Columns className="h-4 w-4" />
              Manage Columns ({getVisibleColumns().length}/{columns.length})
            </button>

            {/* Column Selector Dropdown */}
            {showColumnSelector && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Column Visibility</h3>
                    <button
                      onClick={() => setShowColumnSelector(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Toggle columns to show/hide</p>
                </div>

                <div className="p-2 max-h-96 overflow-y-auto">
                  {columns.map((column) => (
                    <label
                      key={column.key}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={column.visible}
                        onChange={() => toggleColumnVisibility(column.key)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex items-center gap-2 flex-1">
                        {column.visible ? (
                          <EyeIcon className="h-4 w-4 text-blue-600" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        )}
                        <span className={`text-sm ${column.visible ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {column.label}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">#{column.order + 1}</span>
                    </label>
                  ))}
                </div>

                <div className="p-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-xs text-gray-600">
                    {getVisibleColumns().length} of {columns.length} visible
                  </span>
                  <button
                    onClick={resetColumns}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Reset to Default
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Counter */}
      {(filter.health || filter.breed || filter.isActive !== null) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  Found {filteredCattle.length} of {cattle.length} cattle
                </span>
              </div>
            </div>
            <button
              onClick={() => setFilter({ search: '', health: '', breed: '', owner: '', status: '', isActive: null, gaushalaId: undefined })}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Cattle Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {/* Dynamic Column Headers */}
                {getVisibleColumns().map((column) => (
                  <th
                    key={column.key}
                    draggable
                    onDragStart={() => handleDragStart(column.key)}
                    onDragOver={(e) => handleDragOver(e, column.key)}
                    onDrop={() => handleDrop(column.key)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => {
                      // Only sort if not dragging
                      if (!draggedColumn) {
                        handleSort(column.key, e.shiftKey);
                      }
                    }}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-move hover:bg-gray-100 transition-colors select-none ${
                      draggedColumn === column.key ? 'opacity-50 bg-blue-100' : ''
                    } ${
                      dragOverColumn === column.key && draggedColumn !== column.key ? 'border-l-4 border-blue-500' : ''
                    }`}
                    title="Drag to reorder | Click to sort | Shift+Click for multi-column sort"
                  >
                    <div className="flex items-center gap-2">
                      <span className="cursor-grab active:cursor-grabbing">⋮⋮</span>
                      {column.label}
                      {getSortIndicator(column.key)}
                    </div>
                  </th>
                ))}
                {/* Actions Column - Always Visible */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applySorting(filteredCattle).map((cattle) => (
                <tr key={cattle.id} className="hover:bg-gray-50 transition-colors">
                  {/* Dynamic Column Cells */}
                  {getVisibleColumns().map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                      {renderCellContent(column.key, cattle)}
                    </td>
                  ))}
                  {/* Actions Column - Always Visible */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(cattle)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title={t('view')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(cattle)}
                        className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                        title={t('edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleViewAttendance(cattle)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                        title={t('viewAttendance')}
                      >
                        <Clock className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cattle)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title={t('delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {applySorting(filteredCattle).length === 0 && (
          <div className="text-center py-12">
            <span className="text-6xl mx-auto mb-4 block">🐄</span>
            <p className="text-gray-500 text-lg">No cattle found matching your filters</p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (!filter.search || filter.search.trim().length === 0) && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200">
            {/* Page size selector */}
            <div className="flex items-center space-x-2 mb-3 sm:mb-0">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0); // Reset to first page when changing page size
                }}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">
                entries (Total: {totalElements})
              </span>
            </div>

            {/* Page navigation */}
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>

              {/* Page numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage < 3) {
                    pageNum = i;
                  } else if (currentPage > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-green-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              {/* Next button */}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  currentPage >= totalPages - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>

            {/* Page info */}
            <div className="text-sm text-gray-700 mt-3 sm:mt-0">
              Page {currentPage + 1} of {totalPages}
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && selectedCattle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold">{t('cattleDetails')} - {selectedCattle.name}</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">{t('basicInfo')}</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('name')}:</span>
                    <span className="font-medium">{selectedCattle.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('rfidTag')}:</span>
                    <span className="font-mono font-medium">{selectedCattle.rfidTag}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('breed')}:</span>
                    <span className="font-medium">{selectedCattle.breed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('age')}:</span>
                    <span className="font-medium">{selectedCattle.age} {t('years')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('weight')}:</span>
                    <span className="font-medium">{selectedCattle.weight} {t('kg')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('health')}:</span>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getHealthColor(getHealthStatus(selectedCattle))}`}>
                      {t(getHealthStatus(selectedCattle))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('owner')}:</span>
                    <span className="font-medium">{selectedCattle.owner}</span>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">{t('locationInfo')}</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('latitude')}:</span>
                    <span className="font-medium">{selectedCattle.location.latitude.toFixed(6)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('longitude')}:</span>
                    <span className="font-medium">{selectedCattle.location.longitude.toFixed(6)}</span>
                  </div>
                  {selectedCattle.location.address && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('address')}:</span>
                      <span className="font-medium text-right">{selectedCattle.location.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Collection Statistics */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">{t('collectionStats')}</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('totalCollected')}:</span>
                    <span className="font-medium">{selectedCattle.totalDungCollected} {t('kg')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('lastCollection')}:</span>
                    <span className="font-medium">
                      {selectedCattle.lastDungCollection ? formatDate(selectedCattle.lastDungCollection) : t('never')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Medical History */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">{t('medicalHistory')}</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {selectedCattle.medicalHistory.map((record) => (
                    <div key={record.id} className="border-l-4 border-blue-500 pl-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{record.description}</p>
                          <p className="text-sm text-gray-600">{record.veterinarian}</p>
                          {record.medication && (
                            <p className="text-xs text-gray-500">{record.medication}</p>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(record.date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Edit Cattle Modal */}
      {showEditModal && selectedCattle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-semibold">{t('editCattle')} - {selectedCattle.name}</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cattle Name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.name || ''}
                      onChange={(e) => handleEditInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RFID Tag *
                    </label>
                    <input
                      type="text"
                      value={editFormData.rfidTag || ''}
                      onChange={(e) => handleEditInputChange('rfidTag', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Breed *
                    </label>
                    <select
                      value={editFormData.breed || ''}
                      onChange={(e) => handleEditInputChange('breed', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Breed</option>
                      <option value="gir">Gir</option>
                      <option value="sahiwal">Sahiwal</option>
                      <option value="sindhi">Red Sindhi</option>
                      <option value="tharparkar">Tharparkar</option>
                      <option value="holstein">Holstein Friesian</option>
                      <option value="jersey">Jersey</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Health Status
                    </label>
                    <select
                      value={editFormData.health || ''}
                      onChange={(e) => handleEditInputChange('health', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="healthy">Healthy</option>
                      <option value="sick">Sick</option>
                      <option value="under_treatment">Under Treatment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Age (months)
                    </label>
                    <input
                      type="number"
                      value={editFormData.age || ''}
                      onChange={(e) => handleEditInputChange('age', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      value={editFormData.weight || ''}
                      onChange={(e) => handleEditInputChange('weight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {/* Owner Information */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Owner Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Owner Name *
                    </label>
                    <input
                      type="text"
                      value={editFormData.owner || ''}
                      onChange={(e) => handleEditInputChange('owner', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Owner ID/Phone
                    </label>
                    <input
                      type="text"
                      value={editFormData.ownerId || ''}
                      onChange={(e) => handleEditInputChange('ownerId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Location Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={editFormData.location?.address || ''}
                      onChange={(e) => handleEditInputChange('location.address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Latitude
                    </label>
                    <input
                      type="number"
                      value={editFormData.location?.latitude || ''}
                      onChange={(e) => handleEditInputChange('location.latitude', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="any"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Longitude
                    </label>
                    <input
                      type="number"
                      value={editFormData.location?.longitude || ''}
                      onChange={(e) => handleEditInputChange('location.longitude', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="any"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditFormData({});
                  }}
                  disabled={isUpdating}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={isUpdating || !editFormData.name || !editFormData.rfidTag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isUpdating && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isUpdating ? 'Updating...' : 'Update Cattle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cow Dung Transaction Modal */}
      {showTransactionModal && selectedCattle && (
        <CowDungTransaction
          cattle={selectedCattle}
          onClose={() => {
            setShowTransactionModal(false);
            setSelectedCattle(null);
          }}
          onSuccess={handleTransactionSuccess}
        />
      )}

      {/* Transaction History Modal */}
      {showHistoryModal && selectedCattle && (
        <TransactionHistory
          farmerId={selectedCattle.ownerId}
          farmerName={selectedCattle.owner}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedCattle(null);
          }}
        />
      )}

      {/* Daily Attendance Modal */}
      {showAttendanceModal && selectedCattle && attendanceHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full h-[85vh] flex flex-col shadow-sm border border-gray-100">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    {t('attendanceHistory')}
                  </h2>
                  <p className="text-gray-600 mt-1">{selectedCattle.name} - {t('last30Days')}</p>
                </div>
                <button
                  onClick={() => {
                    setShowAttendanceModal(false);
                    setSelectedCattle(null);
                    setAttendanceHistory(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Date Selector */}
              <div>
                <h3 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  📅 {t('selectDate')}
                </h3>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  min={new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400"
                />
              </div>

              {(() => {
                const currentDay = getCurrentDayAttendance();
                if (!currentDay || currentDay.totalLogs.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">📅</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('noDataForDate')}</h3>
                      <p className="text-gray-600 mb-4">{selectedDate === new Date().toISOString().split('T')[0] ? t('today') : new Date(selectedDate).toLocaleDateString()}</p>
                      <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                        {t('absent')}
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    {/* Quick Stats for Selected Date */}
                    <div>
                      <h3 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                        <Activity className="h-5 w-5 text-blue-600" />
                        📊 {t('dayView')}
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <LogIn className="h-5 w-5 text-green-600 mx-auto mb-1" />
                          <div className="text-xs text-gray-600">{t('firstIn')}</div>
                          <div className="text-sm font-bold text-gray-900">
                            {currentDay.firstIn ?
                              new Date(currentDay.firstIn.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                              : '--:--'
                            }
                          </div>
                        </div>

                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <LogOut className="h-5 w-5 text-orange-600 mx-auto mb-1" />
                          <div className="text-xs text-gray-600">{t('lastOut')}</div>
                          <div className="text-sm font-bold text-gray-900">
                            {currentDay.lastOut ?
                              new Date(currentDay.lastOut.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                              : '--:--'
                            }
                          </div>
                        </div>

                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <Activity className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                          <div className="text-xs text-gray-600">{t('totalEntries')}</div>
                          <div className="text-sm font-bold text-gray-900">{currentDay.totalLogs.length}</div>
                        </div>
                      </div>
                    </div>

                    {/* Attendance Summary for Selected Date */}
                    <div>
                      <h3 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        📋 {new Date(selectedDate).toLocaleDateString()}
                        {selectedDate === new Date().toISOString().split('T')[0] && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2">{t('today')}</span>
                        )}
                      </h3>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">{t('firstIn')} Details</label>
                          <div className="text-sm text-gray-900 font-medium">
                            {currentDay.firstIn ?
                              new Date(currentDay.firstIn.timestamp).toLocaleTimeString()
                              : '--'
                            }
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">{t('lastOut')} Details</label>
                          <div className="text-sm text-gray-900 font-medium">
                            {currentDay.lastOut ?
                              new Date(currentDay.lastOut.timestamp).toLocaleTimeString()
                              : '--'
                            }
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">Status</label>
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-sm text-gray-900 font-medium">{t('present')}</span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label className="block text-xs font-medium text-gray-600">Duration</label>
                          <div className="text-sm text-gray-900 font-medium">
                            {currentDay.firstIn && currentDay.lastOut ?
                              `${Math.round((currentDay.lastOut.timestamp - currentDay.firstIn.timestamp) / (1000 * 60 * 60))}h`
                              : '--'
                            }
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Week View */}
                    <div>
                      <h3 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        📅 {t('last7Days')}
                      </h3>
                      <div className="grid grid-cols-7 gap-2">
                        {attendanceHistory.days.slice(0, 7).map((day, index) => (
                          <div
                            key={day.date}
                            className={`p-3 rounded-lg text-center cursor-pointer transition-colors border ${
                              day.date === selectedDate
                                ? 'bg-blue-100 border-blue-200 text-blue-800'
                                : day.totalLogs.length > 0
                                ? 'bg-white border-green-200 hover:bg-green-50'
                                : 'bg-white border-red-200 hover:bg-red-50'
                            }`}
                            onClick={() => handleDateChange(day.date)}
                          >
                            <div className="text-xs font-medium text-gray-600">
                              {new Date(day.date).toLocaleDateString([], { weekday: 'short' })}
                            </div>
                            <div className="text-sm font-bold text-gray-900">
                              {new Date(day.date).getDate()}
                            </div>
                            <div className="text-xs mt-1">
                              {day.totalLogs.length > 0 ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-red-600">✗</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-start gap-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={handleViewAttendanceLogs}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        {t('viewLogs')} ({currentDay.totalLogs.length})
                      </button>

                      <button
                        onClick={() => {
                          setShowAttendanceModal(false);
                          setSelectedCattle(null);
                          setAttendanceHistory(null);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {t('close')}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Attendance Logs Modal */}
      {showAttendanceLogsModal && attendanceLogs.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full h-[85vh] flex flex-col shadow-sm border border-gray-100">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <History className="h-5 w-5 text-blue-600" />
                    </div>
                    {t('attendanceLogs')}
                  </h2>
                  <p className="text-gray-600 mt-1">{selectedCattle?.name} - {new Date(selectedDate).toLocaleDateString()}</p>
                </div>
                <button
                  onClick={() => {
                    setShowAttendanceLogsModal(false);
                    setAttendanceLogs([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Search and Summary */}
              <div className="mb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-md font-semibold text-gray-800 flex items-center gap-2">
                    <Search className="h-4 w-4 text-blue-600" />
                    Search Logs ({getFilteredLogs().length} entries)
                  </h3>
                  <div className="text-sm text-gray-600">
                    Page {logsCurrentPage} of {getTotalPages()}
                  </div>
                </div>

                <input
                  type="text"
                  placeholder="Search by location, type, or time..."
                  value={logsSearchTerm}
                  onChange={(e) => {
                    setLogsSearchTerm(e.target.value);
                    setLogsCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {getPaginatedLogs().length === 0 ? (
                <div className="text-center py-8">
                  <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No logs found</h3>
                  <p className="text-gray-500">
                    {logsSearchTerm ? 'Try adjusting your search terms' : 'No attendance logs for this date'}
                  </p>
                  {logsSearchTerm && (
                    <button
                      onClick={() => {
                        setLogsSearchTerm('');
                        setLogsCurrentPage(1);
                      }}
                      className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {getPaginatedLogs().map((log, index) => (
                  <div key={log.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-xs font-bold text-gray-600">
                        {(logsCurrentPage - 1) * logsPerPage + index + 1}
                      </div>

                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${log.type === 'IN' ? 'bg-green-100' : 'bg-orange-100'}`}>
                          {log.type === 'IN' ? (
                            <LogIn className="h-4 w-4 text-green-600" />
                          ) : (
                            <LogOut className="h-4 w-4 text-orange-600" />
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              log.type === 'IN' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {log.type}
                            </span>
                            <span className="text-sm text-gray-600">{log.location}</span>
                          </div>
                          {log.rfidScanned && (
                            <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                              <Wifi className="h-3 w-3" />
                              RFID Scanned
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="font-mono text-sm font-medium text-gray-900">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowAttendanceLogsModal(false);
                    setAttendanceLogs([]);
                    setLogsCurrentPage(1);
                    setLogsSearchTerm('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('close')}
                </button>

                {getTotalPages() > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setLogsCurrentPage(Math.max(1, logsCurrentPage - 1))}
                      disabled={logsCurrentPage === 1}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, getTotalPages()) }, (_, i) => {
                        let pageNum;
                        if (getTotalPages() <= 5) {
                          pageNum = i + 1;
                        } else if (logsCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (logsCurrentPage >= getTotalPages() - 2) {
                          pageNum = getTotalPages() - 4 + i;
                        } else {
                          pageNum = logsCurrentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setLogsCurrentPage(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                              logsCurrentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setLogsCurrentPage(Math.min(getTotalPages(), logsCurrentPage + 1))}
                      disabled={logsCurrentPage === getTotalPages()}
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}